/**
 * ImageCardRail + ImageCard: 50/50 image / content cards.
 * Mobile:  a native scroll-snap swipe rail; the card nearest the centre
 *          comes forward (scales up, photo turns from grey to colour) and a
 *          counter tracks position.
 * Desktop: the same cards become an editorial stack, alternating sides.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import Button from "./Button";

export function ImageCard({ card, index, active }) {
  const { image, kicker, title, text, cta } = card;
  return (
    <article className={`icard${active ? " is-active" : ""}`} data-icard>
      <div className="icard-media">
        <img src={image.src} alt={image.alt} width={image.w} height={image.h} loading="lazy" decoding="async" />
        <span className="icard-dims">{image.w} × {image.h}</span>
      </div>
      <div className="icard-body">
        <span className="t-cap icard-kicker">{String(index + 1).padStart(2, "0")} — {kicker}</span>
        <h3 className="t-h3 icard-title">{title}</h3>
        <p className="t-body icard-text">{text}</p>
        {cta && <Button variant="text" {...cta.props}>{cta.label}</Button>}
      </div>
    </article>
  );
}

export default function ImageCardRail({ cards }) {
  const railRef = useRef(null);
  const [active, setActive] = useState(0);

  const measure = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const mid = rail.scrollLeft + rail.clientWidth / 2;
    let best = 0, bestD = Infinity;
    Array.from(rail.children).forEach((c, i) => {
      const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid);
      if (d < bestD) { bestD = d; best = i; }
    });
    setActive(best);
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    let raf = 0;
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; measure(); }); };
    rail.addEventListener("scroll", onScroll, { passive: true });
    measure();
    return () => { rail.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [measure]);

  const scrollTo = (i) => {
    const c = railRef.current && railRef.current.children[i];
    if (c) railRef.current.scrollTo({ left: c.offsetLeft - (railRef.current.clientWidth - c.offsetWidth) / 2, behavior: "smooth" });
  };

  return (
    <div className="icards" data-rv="">
      <div className="icards-rail" ref={railRef} role="group" aria-label="Swipe through">
        {cards.map((c, i) => <ImageCard key={c.title} card={c} index={i} active={i === active} />)}
      </div>
      <div className="icards-meter" aria-hidden="false">
        <span className="t-cap">{String(active + 1).padStart(2, "0")} / {String(cards.length).padStart(2, "0")}</span>
        <span className="icards-dots">
          {cards.map((c, i) => (
            <button key={c.title} type="button" className={i === active ? "on" : ""} onClick={() => scrollTo(i)} aria-label={`Show card ${i + 1}`} />
          ))}
        </span>
        <span className="t-cap icards-swipe">Swipe →</span>
      </div>
    </div>
  );
}
